<template>
  <v-card v-if="billingList && billingList.length">
    <v-card-title>
      Faturas
      <v-spacer></v-spacer>
      <v-checkbox
        v-model="force_unification"
        label="Forçar Unificação"
      ></v-checkbox>
      <v-btn
        color="primary"
        @click="runSimulate()"
        :disabled="loading"
        :loading="loading"
        small
        outline
        slot="start"
      >
        Simular
      </v-btn>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Buscar"
        single-line
        hide-details
      ></v-text-field>
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="billingList"
      :loading="loadingInvoices"
      :search="search"
      :pagination.sync="pag"
      select-all="blue"
      @input="selectAllItems"
    >
      <template v-slot:items="props">
        <td class="d-flex justify-center align-center">
          <v-checkbox v-model="props.item.selected"></v-checkbox>
        </td>
        <td class="text-xs-center text-md-center text-lg-center">
          {{ props.item.consumer_unit }}
        </td>
        <td class="text-xs-center text-md-center text-lg-center">
          {{ props.item.competence | formatDate }}
        </td>
        <td class="text-xs-center text-md-center text-lg-center">
          {{ props.item.total | formatCurrency }}
        </td>
        <td class="text-xs-center text-md-center text-lg-center">
          <v-chip
            :color="props.item.status | formatStatusBillingColor"
            text-color="white"
            small
          >
            {{ props.item.status | formatTransactionStatus }}
          </v-chip>
        </td>
        <td class="text-xs-center text-md-center text-lg-center">
          {{ getDueDate(props.item) | formatDate }}
        </td>
        <td class="text-xs-center text-md-center text-lg-center">
          <v-tooltip
            v-if="(isAdmin || isFinancial) && props.item.unique_billing"
            bottom
          >
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" color="green darken-4">task_alt</v-icon>
            </template>
            <span>Conta unificada</span>
          </v-tooltip>
          <v-tooltip
            v-else-if="(isAdmin || isFinancial) && !props.item.unique_billing"
            bottom
          >
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" color="error">highlight_off</v-icon>
            </template>
            <span>A conta não é unificada</span>
          </v-tooltip>
        </td>
        <td class="text-xs-center text-md-center text-lg-center">
          {{ props.item.power_plant_id }}
        </td>
      </template>

      <template v-slot:no-data>
        <v-alert :value="true" color="error" icon="warning">
          Sem dados para exibir.
        </v-alert>
      </template>

      <template v-slot:no-results>
        <v-alert :value="true" color="error" icon="warning">
          Sua pesquisa por "{{ search }}" não encontrou nenhum resultado.
        </v-alert>
      </template>
    </v-data-table>

    <v-dialog v-model="simulateDialog" max-width="500px" lazy>
      <v-card>
        <v-card-title class="py-2">
          <v-layout align-center justify-end row fill-height>
            <div class="headline font-weight-bold mx-auto">Simulação</div>
          </v-layout>
        </v-card-title>

        <v-list two-line>
          <v-list-tile>
            <v-list-tile-content>
              <v-list-tile-title>
                Valor Juntos: {{ simulateResult.energy_value | formatCurrency }}
              </v-list-tile-title>
              <v-list-tile-title>
                Valor Concessionária:
                {{ simulateResult.utility_value | formatCurrency }}
              </v-list-tile-title>
              <v-list-tile-title>
                Valor Total: {{ simulateResult.total_amount | formatCurrency }}
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </v-list>

        <v-list>
          <v-list-group>
            <template v-slot:activator>
              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-title>Valores por Usina</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </template>

            <v-list-tile
              v-for="plant in Object.keys(simulateResult.values_per_plant)"
              :key="plant"
            >
              <v-list-tile-content>
                <v-list-tile-title>
                  {{ plant }}:
                  {{ simulateResult.values_per_plant[plant] | formatCurrency }}
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list-group>
        </v-list>

        <v-card-actions>
          <v-layout column align-space-between justify-space-between>
            <v-flex>
              <v-menu
                v-model="menu"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="due_date"
                    label="Nova data de vencimento"
                    prepend-icon="event"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  dark
                  v-model="due_date"
                  @input="menu = false"
                ></v-date-picker>
              </v-menu>
            </v-flex>
            <v-flex>
              <v-btn color="red" text @click="simulateDialog = false">
                Cancelar
              </v-btn>

              <v-btn color="green" :loading="loading" text @click="runMerge()">
                Gerar Boleto
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="resultDialog" max-width="500px" lazy>
      <v-card>
        <v-card-title class="py-2">
          <v-layout align-center justify-end row fill-height>
            <div class="headline font-weight-bold mx-auto">Resultado</div>
          </v-layout>
        </v-card-title>

        <v-card-text>
          <v-layout align-center justify-center row fill-height wrap>
            <v-flex>
              <a :href="realResult.boleto_url" target="_blank">
                <v-btn color="primary" :disabled="!realResult.boleto_url"
                  >Visualizar Boleto</v-btn
                >
              </a>
            </v-flex>
            <v-flex md4>
              <a :href="realResult.spreadsheet_url" target="_blank">
                <v-btn color="primary" :disabled="!realResult.spreadsheet_url"
                  >Baixar Planilha</v-btn
                >
              </a>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <div class="mx-auto">
            <v-btn color="red" text @click="resultDialog = false">
              Fechar
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar :color="color" top v-model="snackbar" dark>
      <v-icon color="white" class="mr-3"> mdi-bell-plus </v-icon>
      <div>{{ snackMessage }}</div>
      <v-icon size="16" @click="snackbar = false"> mdi-close-circle </v-icon>
    </v-snackbar>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import { joinedBilling, getDebtBillingById } from "../../services/billing";

export default {
  data() {
    return {
      search: "",
      headers: [
        {
          text: "Unidade Consumidora",
          value: "consumer_unit",
          align: "center",
        },
        { text: "Competência", value: "competence", align: "center" },
        { text: "Valor da Fatura", value: "total", align: "center" },
        { text: "Status", value: "status", align: "center" },
        { text: "Data de Vencimento", value: "due_date", align: "center" },
        { text: "Conta Unificada", value: "unique_billing", align: "center" },
        { text: "Usina", value: "power_plant_id", align: "center" },
      ],
      pag: {
        sortBy: "consumer_unit",
        rowsPerPage: 20,
      },
      billingList: [],
      simulateResult: {
        energy_value: null,
        utility_value: null,
        total_amount: null,
        values_per_plant: {},
      },
      realResult: {
        debt_billing_id: null,
        spreadsheet_url: null,
      },
      customer_id: null,
      simulate: true,
      due_date: null,
      force_unification: true,
      simulateDialog: false,
      resultDialog: false,
      loading: false,
      menu: false,
      color: null,
      snackbar: false,
      snackMessage: null,
      selectAll: false,
    };
  },

  watch: {
    mergingInvoices() {
      if (this.mergingInvoices.length)
        this.customer_id = this.mergingInvoices[0].customer_id;

      this.billingList = this.mergingInvoices.map((item) => {
        return {
          ...item,
          selected: false,
        };
      });
    },
  },

  computed: {
    ...mapState("billing", ["loadingInvoices", "mergingInvoices"]),
    ...mapState(["user", "permissions"]),

    selectedBillings() {
      return this.billingList
        .filter((item) => item.selected)
        .map((item) => item.id);
    },

    isAdmin() {
      let currentUserGroups = this.user.group.toString();
      return currentUserGroups.indexOf("admin") >= 0;
    },

    isFinancial() {
      let currentUserGroups = this.user.group.toString();
      return currentUserGroups.indexOf("financial") >= 0;
    },

    isPartnerManagement() {
      let currentUserGroups = this.user.group.toString();
      return currentUserGroups.indexOf("partner_management") >= 0;
    },
  },

  filters: {
    formatCurrency: (value) => {
      if (!value) return "-";
      return value.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },

    formatDate: (date) => {
      if (!date) return "-";
      return date.split("-").reverse().join("/");
    },
  },

  methods: {
    snack(message, color) {
      this.color = color;
      this.snackMessage = message;
      this.snackbar = true;
    },

    async mergeBillings() {
      const response = await joinedBilling(
        this.simulate,
        this.customer_id,
        this.force_unification,
        this.due_date,
        this.selectedBillings,
      );
      return response.data;
    },

    async runSimulate() {
      this.loading = true;
      this.simulate = true;
      const result = await this.mergeBillings();
      this.simulateResult = result;
      this.loading = false;
      this.simulateDialog = true;
    },

    async runMerge() {
      this.loading = true;
      this.simulate = false;
      const result = await this.mergeBillings();
      this.realResult = result;

      await new Promise((resolve) => setTimeout(resolve, 10000));
      const billing = await getDebtBillingById(this.realResult.debt_billing_id);
      const boleto_url = billing.data().transaction?.boleto_url;
      this.realResult = {
        ...this.realResult,
        boleto_url: boleto_url,
      };
      this.simulateDialog = false;
      this.loading = false;
      this.resultDialog = true;
    },

    isEmptyObject(obj) {
      return Object.keys(obj).length === 0;
    },
    selectAllItems() {
      this.selectAll = !this.selectAll;
      this.billingList = this.billingList.map((item) => {
        return {
          ...item,
          selected: this.selectAll,
        };
      });
    },
    getDueDate(billing) {
      const transaction = billing.transaction;
      let due_date = billing.due_date;

      if (transaction) {
        if (transaction.payment_method == "boleto") {
          due_date = transaction.boleto_expiration_date.substring(0, 10);
        }
        if (transaction.payment_method == "pix") {
          due_date = transaction.pix_expiration_date.substring(0, 10);
        }
        if (transaction.payment_method == "credit_card") {
          due_date = billing.charge_at;
        }
      }
      return due_date;
    },
  },
};
</script>
